import { Menu } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useWakeLock } from '../hooks/useWakeLock.ts';
import { useAuth } from '../lib/auth.tsx';
import { amorcerPosition } from '../lib/courses.ts';
import { useSettings } from '../lib/settings.tsx';
import { useUpdates } from '../lib/updates.tsx';
import { EtatReseau } from './EtatReseau.tsx';
import { NotificationBell } from './NotificationBell.tsx';
import { Avatar } from './Avatar.tsx';
import { Drawer } from './Drawer.tsx';
import { NotificationToast } from './NotificationToast.tsx';

export function AppShell() {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const { settings } = useSettings();
    const { user } = useAuth();
    const updates = useUpdates();
    /* Deux écrans portent le fond fairway sur toute leur hauteur : l'accueil,
     * qu'on regarde avant d'agir, et le tour en cours, où il fait RESSORTIR
     * les cartes blanches — bandeau du trou, ruban de score, carte du club,
     * pavé — au lieu de les laisser flotter sur un papier de même valeur.
     *
     * La lisibilité en plein soleil n'en souffre pas : ce qui se lit vit dans
     * les cartes, et elles restent claires. Le fond n'est que l'écart entre
     * elles. La barre du haut s'efface alors dans la même teinte plutôt que
     * de trancher en bandeau clair.
     *
     * Ailleurs — saisie, listes, réglages — l'habillage reste neutre : ces
     * écrans se lisent d'un bout à l'autre, pas par blocs. */
    const cheminActuel = useLocation().pathname;
    const surFairway = cheminActuel === '/'
        || cheminActuel === '/parcours/jouer'
        || cheminActuel === '/entrainement/exercices';

    /* La barre système du téléphone suit le fond de la page.
     *
     * Deux valeurs mesurées, pas devinées : le haut du dégradé fairway
     * rastérisé (#173523) sur l'accueil, --color-base-100 rastérisé
     * (#f3f8f4, celui d'index.html) partout ailleurs. Sans cette bascule,
     * une encoche claire surmonterait le vert de l'accueil — précisément le
     * raccord visible qu'on vient de supprimer dans la page. */
    useEffect(() => {
        document.querySelector('meta[name="theme-color"]')
            ?.setAttribute('content', surFairway ? '#173523' : '#f3f8f4');
    }, [surFairway]);

    // La position est prise au lancement quand la permission est deja
    // accordee : la liste des parcours n'a plus a l'attendre. Elle ne
    // DEMANDE jamais la permission ici — voir amorcerPosition.
    useEffect(amorcerPosition, []);

    // Le verrou d'écran est monté ici, au-dessus des pages : il survit à la
    // navigation entre l'écran de saisie et la synthèse, sans quoi il serait
    // relâché puis repris à chaque changement de route.
    useWakeLock(settings.wakeLockEnabled);

    return (
        /* Le fond fairway est peint ICI, sur l'enveloppe pleine hauteur, et
         * non dans la page : c'est la seule façon qu'il couvre l'écran du
         * tout premier pixel — sous la barre transparente — jusqu'au tout
         * dernier, même quand le contenu est plus court que l'écran. Peint
         * dans Home, il fallait des marges négatives calées sur la hauteur
         * de la barre, et il s'arrêtait avec le contenu — le raccord se
         * voyait en haut comme en bas. Un seul élément, un seul dégradé,
         * zéro raccord. */
        <div className={`min-h-full md:pl-72 ${surFairway ? 'fairway-texture' : ''}`}>
            <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />

            {/* La bulle est montée ici, au-dessus des pages : une invitation
                doit se voir aussi bien depuis un parcours en cours que depuis
                l'accueil, et elle survit à la navigation. */}
            <NotificationToast />

            {/* Barre supérieure : uniquement sur mobile, le tiroir étant
                permanent au-delà de md.
             *
             * Sur l'accueil elle est TRANSPARENTE, sans aplat ni flou : le
             * fond fairway est peint sur l'enveloppe ci-dessus et passe
             * derrière elle, si bien que la barre et le bandeau de
             * salutation ne sont pas deux surfaces raccordées mais une seule
             * et même texture, peinte une fois. Un aplat même translucide
             * aurait créé une COUTURE — une bande à peine plus sombre au
             * raccord — parce qu'une bordure adoucie au flou ne peut jamais
             * retomber exactement sur la teinte du dégradé en dessous.
             *
             * L'ombre portée sur les icônes est la contrepartie : sans elle,
             * un menu ou une carte claire qui remonterait sous la barre au
             * défilement rendrait les icônes blanches invisibles.
             *
             * `[&_.btn]:!text-white` — le hamburger et la cloche sont des
             * `.btn-ghost` daisyUI, qui posent LEUR PROPRE couleur de texte
             * (celle du thème). Cette couleur gagnait sur le blanc hérité,
             * rendant les icônes noires sur le vert en thème clair — d'où le
             * forçage en `!important`, seul moyen de l'emporter sans toucher
             * au composant partagé `NotificationBell`. */}
            <header className={`safe-top sticky top-0 z-30 flex items-center gap-3 px-3 pb-2 md:hidden ${
                surFairway
                    ? 'text-white [&_svg]:drop-shadow-[0_1px_3px_rgba(0,0,0,0.55)] [&_.btn]:!text-white'
                    : 'bg-base-100/90 backdrop-blur'}`}>
                <button
                    type="button"
                    className="btn btn-ghost btn-square tap-target"
                    onClick={() => setDrawerOpen(true)}
                    aria-label="Ouvrir le menu"
                >
                    <Menu size={24} />
                </button>
                {/* Le titre ramène à l'accueil : c'est ce que tout le monde
                    essaie en premier pour sortir d'un écran, et il n'y avait
                    jusqu'ici que le tiroir pour le faire. */}
                <Link to="/" className={`font-display flex-1 text-lg font-bold tracking-tight ${
                    surFairway ? 'drop-shadow-[0_1px_3px_rgba(0,0,0,0.55)]' : ''}`}>⛳ Golf</Link>

                {/* Avant la cloche : ce qui concerne la saisie en cours passe
                    devant ce qui concerne les autres. */}
                <EtatReseau />
                <NotificationBell />
                {/* L'avatar dans la barre : il dit d'un coup d'œil qui est
                    connecté, ce qui compte dès qu'un téléphone sert à deux
                    joueurs, et il ouvre le tiroir comme le bouton menu. */}
                <button type="button" onClick={() => setDrawerOpen(true)} aria-label="Ouvrir le menu">
                    <Avatar name={user?.displayName ?? ''} email={user?.email}
                        avatarUrl={user?.avatarUrl} size={32} />
                </button>
            </header>

            {/* La bande de mise à jour est rendue au niveau de l'application,
                pas ici. Reste sa compensation : sans elle, le bas du contenu
                passerait sous la bande, qui est en position fixe. */}
            <main className={`mx-auto w-full max-w-3xl px-4 pt-2 md:pt-6 ${
                updates.ready ? 'pb-28' : 'pb-8'}`}>
                <Outlet />
            </main>
        </div>
    );
}
